import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '@/services/api';
import { useLanguage } from '@/state/language/LanguageProvider';

interface HierarchyNode {
  id: string;
  name: string;
  level_type: string;
  level: number;
  parent_id?: string;
  children?: HierarchyNode[];
}

interface CopyFromHierarchyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialHierarchyType?: 'BUSINESS' | 'FUNCTIONAL' | 'GEOGRAPHIC';
  initialHierarchy?: HierarchyNode[];
  editTemplateId?: string; // If provided, modal is in edit mode
  editTemplateName?: string; // Template name for edit mode
  editTemplateDescription?: string; // Template description for edit mode
}

export const CopyFromHierarchyModal: React.FC<CopyFromHierarchyModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialHierarchyType = 'BUSINESS',
  initialHierarchy,
  editTemplateId,
  editTemplateName,
  editTemplateDescription,
}) => {
  const { t } = useLanguage();
  const [hierarchyType, setHierarchyType] = useState<'BUSINESS' | 'FUNCTIONAL' | 'GEOGRAPHIC'>(initialHierarchyType);
  const [hierarchy, setHierarchy] = useState<HierarchyNode[]>([]);
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'copy' | 'create'>('copy'); // 'copy' = copy from existing, 'create' = create new

  // Transform flat data into hierarchical structure
  const buildHierarchy = (flatData: any[]): HierarchyNode[] => {
    const nodeMap = new Map<string, HierarchyNode>();
    const rootNodes: HierarchyNode[] = [];

    // First pass: create all nodes with children array
    flatData.forEach(node => {
      nodeMap.set(node.id, {
        ...node,
        children: []
      });
    });

    // Second pass: build parent-child relationships
    flatData.forEach(node => {
      const nodeWithChildren = nodeMap.get(node.id)!;
      
      if (node.parent_id) {
        const parent = nodeMap.get(node.parent_id);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(nodeWithChildren);
        }
      } else {
        rootNodes.push(nodeWithChildren);
      }
    });

    return rootNodes;
  };

  useEffect(() => {
    if (isOpen) {
      // Reset form state when modal opens
      if (editTemplateId) {
        // Edit mode: load template data
        setTemplateName(editTemplateName || '');
        setDescription(editTemplateDescription || '');
        setMode('copy'); // Use copy mode for editing
        loadTemplateForEdit();
      } else {
        setTemplateName('');
        setDescription('');
        setError(null);
        setSelectedNodes(new Set());
        
        if (mode === 'create') {
          // In create mode, don't load hierarchy data
          setHierarchy([]);
          setLoading(false);
        } else if (initialHierarchy) {
          // Use provided hierarchy directly
          setHierarchy(initialHierarchy);
          // Auto-select all nodes
          const allNodeIds = new Set<string>();
          const collectNodeIds = (nodes: HierarchyNode[]) => {
            nodes.forEach(node => {
              allNodeIds.add(node.id);
              if (node.children) {
                collectNodeIds(node.children);
              }
            });
          };
          collectNodeIds(initialHierarchy || []);
          setSelectedNodes(allNodeIds);
          setLoading(false);
        } else {
          // Load hierarchy from API
          loadHierarchy();
        }
      }
    }
  }, [isOpen, hierarchyType, initialHierarchy, mode, editTemplateId, editTemplateName, editTemplateDescription]);

  // Load template content for editing
  const loadTemplateForEdit = async () => {
    if (!editTemplateId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch template nodes from API
      const response = await api.get(`/hierarchy-template-management/templates/${editTemplateId}/nodes`);
      
      if (response.data.success && response.data.data) {
        const nodes = response.data.data; // API returns { success: true, data: nodes[] }
        // Convert flat nodes to hierarchical structure
        const hierarchicalNodes = buildHierarchy(nodes);
        setHierarchy(hierarchicalNodes);
        
        // Auto-select all nodes
        const allNodeIds = new Set<string>();
        const collectNodeIds = (nodeList: HierarchyNode[]) => {
          nodeList.forEach(node => {
            allNodeIds.add(node.id);
            if (node.children) {
              collectNodeIds(node.children);
            }
          });
        };
        collectNodeIds(hierarchicalNodes);
        setSelectedNodes(allNodeIds);
        
        // Also fetch template details to get hierarchy_type
        try {
          const templateResponse = await api.get(`/hierarchy-template-management/templates/${editTemplateId}`);
          if (templateResponse.data.success && templateResponse.data.data) {
            const template = templateResponse.data.data;
            if (template.hierarchy_type) {
              setHierarchyType(template.hierarchy_type);
            }
          }
        } catch (err) {
          // If we can't get template details, continue with default hierarchy type
          console.warn('Could not fetch template details for hierarchy type:', err);
        }
      } else {
        setError('Failed to load template data');
      }
    } catch (error: any) {
      console.error('Error loading template for edit:', error);
      setError(error.response?.data?.error || 'Failed to load template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadHierarchy = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/hierarchy/${hierarchyType.toLowerCase()}`);
      const flatData = response.data.data || response.data;
      
      // Transform flat data into hierarchical structure
      const hierarchicalData = buildHierarchy(flatData || []);
      setHierarchy(hierarchicalData);
      
      // Auto-select all nodes by default
      const allNodeIds = new Set<string>();
      const collectNodeIds = (nodes: HierarchyNode[]) => {
        nodes.forEach(node => {
          allNodeIds.add(node.id);
          if (node.children) {
            collectNodeIds(node.children);
          }
        });
      };
      collectNodeIds(hierarchicalData || []);
      setSelectedNodes(allNodeIds);
    } catch (err) {
      setError(t('hierarchy.copyFromModal.loadError'));
      console.error('Error loading hierarchy:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNodeToggle = (nodeId: string) => {
    const newSelected = new Set(selectedNodes);
    if (newSelected.has(nodeId)) {
      newSelected.delete(nodeId);
    } else {
      newSelected.add(nodeId);
    }
    setSelectedNodes(newSelected);
  };

  const handleSelectAll = () => {
    const allNodeIds = new Set<string>();
    const collectNodeIds = (nodes: HierarchyNode[]) => {
      nodes.forEach(node => {
        allNodeIds.add(node.id);
        if (node.children) {
          collectNodeIds(node.children);
        }
      });
    };
    collectNodeIds(hierarchy);
    setSelectedNodes(allNodeIds);
  };

  const handleSelectNone = () => {
    setSelectedNodes(new Set());
  };

  const handleCreateTemplate = async () => {
    if (!templateName.trim()) {
      setError('Template name is required');
      return;
    }

    if (mode === 'copy' && selectedNodes.size === 0) {
      setError('Please select at least one node to copy');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      if (editTemplateId) {
        // Edit mode: update existing template
        const filteredHierarchy = filterHierarchyBySelection(hierarchy, selectedNodes);
        
        const response = await api.put(`/hierarchy-template-management/templates/${editTemplateId}`, {
          name: templateName,
          description: description,
          hierarchy_type: hierarchyType,
          hierarchy_data: filteredHierarchy
        });

        if (response.data.success) {
          onSuccess();
          onClose();
          alert('Template updated successfully!');
        } else {
          setError('Failed to update template');
        }
      } else if (mode === 'copy') {
        // Copy from existing hierarchy
        const filteredHierarchy = filterHierarchyBySelection(hierarchy, selectedNodes);

        const response = await api.post('/hierarchy-template-management/templates', {
          name: templateName,
          description: description || `Template created from my ${hierarchyType.toLowerCase()} hierarchy`,
          hierarchy_type: hierarchyType,
          is_public: true,
          copy_from_hierarchy: true,
          hierarchy_data: filteredHierarchy
        });

        if (response.data.success) {
          onSuccess();
          onClose();
          alert('Template created successfully!');
        } else {
          setError('Failed to create template');
        }
      } else {
        // Create new empty template (nodes can be added later via template editing)
        const response = await api.post('/hierarchy-template-management/templates', {
          name: templateName,
          description: description || `New ${hierarchyType.toLowerCase()} hierarchy template`,
          hierarchy_type: hierarchyType,
          is_public: true,
          copy_from_hierarchy: false,
          hierarchy_data: []
        });

        if (response.data.success) {
          onSuccess();
          onClose();
          alert('Template created successfully! You can now add hierarchy nodes to it.');
        } else {
          setError('Failed to create template');
        }
      }
    } catch (error: any) {
      console.error('Error saving template:', error);
      setError(error.response?.data?.error || 'Failed to save template. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const filterHierarchyBySelection = (nodes: HierarchyNode[], selectedIds: Set<string>): HierarchyNode[] => {
    return nodes
      .filter(node => selectedIds.has(node.id))
      .map(node => ({
        ...node,
        children: node.children ? filterHierarchyBySelection(node.children, selectedIds) : undefined
      }));
  };

  const renderHierarchyNode = (node: HierarchyNode, level: number = 0) => {
    const isSelected = selectedNodes.has(node.id);
    
    return (
      <div key={node.id}>
        <div className="flex items-center py-1" style={{ marginLeft: `${level * 20}px` }}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => handleNodeToggle(node.id)}
            className="mr-2"
          />
          <span className="text-sm">
            {node.name} ({node.level_type}) - Level {node.level}
          </span>
        </div>
        {node.children && node.children.map(child => renderHierarchyNode(child, level + 1))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
{t('hierarchy.copyFromModal.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Mode Selection: Copy from Existing or Create New */}
          <div className="border-b border-gray-200">
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  setMode('copy');
                  // When switching to copy mode, load hierarchy if not already loaded
                  if (!initialHierarchy && hierarchy.length === 0) {
                    loadHierarchy();
                  }
                }}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  mode === 'copy'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Copy from Existing Hierarchy
              </button>
              <button
                onClick={() => {
                  setMode('create');
                  // When switching to create mode, clear hierarchy and selections
                  setHierarchy([]);
                  setSelectedNodes(new Set());
                  setLoading(false);
                }}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  mode === 'create'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Create New Hierarchy Template
              </button>
            </div>
          </div>

          {/* Hierarchy Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hierarchy Type *
            </label>
            <select
              value={hierarchyType}
              onChange={(e) => setHierarchyType(e.target.value as 'BUSINESS' | 'FUNCTIONAL' | 'GEOGRAPHIC')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="BUSINESS">Business Hierarchy</option>
              <option value="FUNCTIONAL">Functional Hierarchy</option>
              <option value="GEOGRAPHIC">Geographic Hierarchy</option>
            </select>
          </div>

          {/* Template Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
{t('hierarchy.copyFromModal.templateName')} *
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder={t('hierarchy.copyFromModal.enterTemplateName')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
{t('hierarchy.copyFromModal.description')}
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('hierarchy.copyFromModal.enterDescription')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Content based on mode */}
          {mode === 'copy' ? (
            /* Copy from Existing Hierarchy */
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Select Nodes to Copy
                </label>
                <div className="space-x-2">
                  <button
                    onClick={handleSelectAll}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Select All
                  </button>
                  <button
                    onClick={handleSelectNone}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Select None
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-gray-500">Loading hierarchy...</div>
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              ) : (
                <div className="border border-gray-200 rounded-md p-4 max-h-60 overflow-y-auto">
                  {hierarchy.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      No hierarchy data available. Please create a hierarchy first.
                    </div>
                  ) : (
                    hierarchy.map(node => renderHierarchyNode(node))
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Create New Hierarchy */
            <div>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> After creating the template, you can edit it to add hierarchy nodes. 
                  For now, create the template with a basic structure and add nodes later, or use &quot;Copy from Existing&quot; 
                  to start with an existing hierarchy structure.
                </p>
              </div>
              <div className="border border-gray-200 rounded-md p-4">
                <p className="text-sm text-gray-600 text-center py-4">
                  Template will be created with the selected hierarchy type. You can add nodes after creation.
                </p>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              disabled={creating}
            >
              Cancel
            </button>
            <button
              onClick={handleCreateTemplate}
              disabled={creating || !templateName.trim() || (mode === 'copy' && selectedNodes.size === 0)}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                !templateName.trim() 
                  ? 'Please enter a template name' 
                  : mode === 'copy' && selectedNodes.size === 0
                  ? 'Please select at least one node to copy'
                  : 'Create template'
              }
            >
              {creating ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
